/**
 * DLP attribution watermark.
 *
 * Renders an SVG-based diagonal-tiled overlay across the entire viewport
 * (and onto every printed page) carrying the current user's email and a
 * UTC timestamp. The pattern is repeated at 30° so cropping out one
 * instance does not remove all — partial crops still leak the user's
 * identity into the screenshot.
 *
 * Charter framing: this is a deterrent, not a security boundary. A
 * determined adversary can:
 *   - blur or crop tiles in image editing software
 *   - run optical text removal
 *   - take a phone photo of a single isolated detail
 * What we are defending against is the casual screenshot-and-paste
 * exfiltration that watermarking neutralises by attribution.
 *
 * Implementation:
 *   - `position: fixed; inset: 0; pointer-events: none; z-index: 9999`
 *   - `mix-blend-mode: multiply` keeps the text legible on light
 *     backgrounds and dark on dark, without obstructing data
 *   - `opacity: 0.04` after Pedro's first click-test (was 0.07). Still
 *     legible when screenshotted at full resolution but much less
 *     distracting in daily use. Print mode amplifies to 0.18 (see
 *     dlp/dlp.css @media print) so the printed warning page carries
 *     a strong attribution stamp.
 *   - `@media print` overrides give a stronger watermark on printed
 *     output (which is already neutered to a "restricted" notice; the
 *     watermark adds attribution to the restricted notice itself).
 *   - Refreshes the timestamp every minute so the captured time is
 *     never older than 60 s.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCurrentUser } from '../hooks/useAuth';

function formatStamp(d: Date): string {
  // Compact but human-readable: 2026-05-03 14:32 UTC
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min} UTC`;
}

export function Watermark() {
  const { user } = useCurrentUser();
  const [stamp, setStamp] = useState(() => formatStamp(new Date()));

  // Tick the timestamp every 60 seconds so the watermark on a
  // long-lived tab doesn't preserve a misleading earlier time.
  useEffect(() => {
    const id = window.setInterval(() => setStamp(formatStamp(new Date())), 60_000);
    return () => window.clearInterval(id);
  }, []);

  if (!user) return null;

  const label = `${user.email} · ${stamp} · Baluarte CRM`;
  // SVG-as-data-URI tile. 460×260 spacing, text rotated -30deg.
  // The padding avoids the pattern joining at the edges in a way that
  // would create a visible grid line.
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="460" height="260">
      <g transform="rotate(-30 230 130)">
        <text x="50%" y="50%"
              fill="#0e3247"
              font-family="system-ui,sans-serif"
              font-size="16"
              font-weight="500"
              text-anchor="middle"
              dominant-baseline="middle"
              opacity="1">
          ${label.replace(/&/g, '&amp;').replace(/</g, '&lt;')}
        </text>
      </g>
    </svg>
  `.trim();
  const dataUri = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

  // Portal directly into <body> — the @media print rule in dlp/dlp.css
  // hides body's React-tree children (#root) so the print preview is
  // free of the regular UI. The watermark needs to be a sibling of
  // #root, not a descendant, so it survives that rule and amplifies
  // on the printed output.
  return createPortal(
    <div
      id="__baluarte-watermark"
      aria-hidden="true"
      data-baluarte-watermark="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: 0.04,
        backgroundImage: dataUri,
        backgroundRepeat: 'repeat',
        // Multiply blends the text so it looks like a faint stamp on
        // any background — light grey on white, faint blue on dark.
        mixBlendMode: 'multiply',
      }}
    />,
    document.body,
  );
}
